import { FC, useEffect, useMemo, useRef } from 'react';

import { IconArrowsMaximize, IconArrowsMinimize } from '@tabler/icons-react';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import * as echarts from 'echarts';
import { ModalNotStyled } from 'ui-kit';

import { ChartSkeletonLoader } from './ChartSkeleton';
import { chartColors } from '../../../../configs/chart.config';
import { IconButton } from '../../../../ui-kit';
import { QUERY_KEYS } from '../shared';

type MortgageCreditCurveData = {
  labels: string[];
  series: {
    [key: string]: number[];
  };
};

type MortgageCreditCurveProps = {
  dataUrl?: string;
  width?: number;
  height?: number;
  className?: string;
  modalView?: boolean;
  onExpandClose?: () => void;
};

export const MortgageCreditCurveChart: FC<MortgageCreditCurveProps> = ({
  dataUrl,
  width = 620,
  height = 382,
  className = '',
  modalView = false,
  onExpandClose,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const { data, isLoading } = useReactQuery<MortgageCreditCurveData, Error>({
    queryKey: [QUERY_KEYS.MORTGAGE_CREDIT_CURVE, dataUrl],
    queryFn: async () => {
      if (!dataUrl) throw new Error('No URL provided');
      const response = await fetch(dataUrl);
      return response.json();
    },
    enabled: !!dataUrl,
  });

  const makeRichKey = (name: string) => name.replace(/\s+/g, '_');

  const chartOption = useMemo(() => {
    if (!data) return null;

    const { labels, series } = data;
    const seriesNames = Object.keys(series);
    const allValues = seriesNames.flatMap((key) => series[key]);

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    const axisTop = (maxValue + 0.1).toFixed(1);
    const axisBottom = (minValue - 0.1).toFixed(1);

    const seriesData = seriesNames.map((key, index) => ({
      name: key,
      type: 'line',
      data: series[key],
      showSymbol: true,
      symbolSize: 6,
      smooth: false,
      lineStyle: {
        width: 2,
        shadowBlur: 6,
        shadowColor: 'rgba(132, 144, 236, 0.53)',
      },
      itemStyle: {
        color: chartColors.colorPalette[index % chartColors.colorPalette.length],
        borderColor: '#fff',
        borderWidth: 1,
      },
      emphasis: {
        itemStyle: {
          color: chartColors.colorPalette[index % chartColors.colorPalette.length],
          borderColor: '#fff',
          borderWidth: 2,
        },
      },
    }));

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(66,66,66,0.95)',
        borderWidth: 0,
        textStyle: {
          color: '#fff',
          fontWeight: 'bold',
        },
        axisPointer: {
          type: 'line',
          lineStyle: {
            type: 'solid',
            color: '#BBC5D7',
            width: 1,
          },
        },
        formatter: (params: any) => {
          if (!params || !params.length) return '';
          let tooltipText = `
            <div style="border-radius: 0.5rem;">
              <p style="
                margin: 0;
                font-weight: bold;
                font-size: 12px;
                color: white;
              ">${params[0].axisValue}</p>
          `;
          params.forEach((item: any) => {
            const value = typeof item.data === 'number' ? item.data.toFixed(2) : item.data;
            tooltipText += `
              <p style="
                font-weight: bold;
                font-size: 12px;
                color: ${item.color};
              ">${item.seriesName}: ${value}</p>
            `;
          });
          tooltipText += '</div>';
          return tooltipText;
        },
      },
      legend: {
        top: 5,
        itemGap: 30,
        itemWidth: 20,
        itemHeight: 8,
        formatter: (name: string) => `{${makeRichKey(name)}|${name}}`,
        textStyle: {
          rich: seriesNames.reduce(
            (acc, name, index) => {
              acc[makeRichKey(name)] = {
                fontSize: 12,
                color: chartColors.colorPalette[index % chartColors.colorPalette.length],
              };
              return acc;
            },
            {} as Record<string, any>,
          ),
        },
      },
      grid: {
        top: 35,
        left: '4%',
        right: '4%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        name: 'Credit Score Range',
        boundaryGap: false,
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#BBC5D7',
          fontSize: 12,
          padding: [20, 0, 0, 0],
        },
        axisLabel: {
          color: '#BBC5D7',
          margin: 16,
        },
        axisLine: {
          lineStyle: { color: '#868E9F' },
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#28303E' },
        },
      },
      yAxis: {
        interval: 0.1,
        type: 'value',
        min: axisBottom,
        max: axisTop,
        name: 'Interest Rate (%)',
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#BBC5D7',
          fontSize: 12,
          padding: [0, 0, 30, 0],
        },
        axisLabel: {
          color: '#BBC5D7',
          formatter: (val: number) => val.toFixed(1),
          margin: 16,
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#868E9F' },
        },
        axisTick: {
          show: true,
          length: 6,
          lineStyle: {
            color: '#BBC5D7',
            width: 1,
          },
        },
        splitLine: {
          lineStyle: { color: '#28303E' },
        },
      },
      series: seriesData,
    };
  }, [data]);

  useEffect(() => {
    if (chartRef.current && chartOption) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }
      chartInstance.current.setOption(chartOption);
    }

    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [chartOption]);

  if (isLoading || !data) {
    return (
      <div className="mt-16">
        <ChartSkeletonLoader width={width} height={height} />
      </div>
    );
  }

  return (
    <div className={`relative mx-auto mt-16 w-fit rounded-lg bg-slate-950 px-4 py-3 ${className}`}>
      {!modalView ? (
        <ModalNotStyled
          trigger={
            <IconButton
              variant="icon-only"
              icon={<IconArrowsMaximize size={16} color="#e85ee5" />}
              className="absolute right-2 rounded-full p-2"
              onClick={() => {}}
            />
          }
        >
          {({ closeModal }) => (
            <MortgageCreditCurveChart
              dataUrl={dataUrl}
              modalView={true}
              onExpandClose={closeModal}
              width={window.innerWidth * 0.8}
              height={window.innerHeight * 0.8}
            />
          )}
        </ModalNotStyled>
      ) : (
        <IconButton
          variant="icon-only"
          icon={<IconArrowsMinimize size={16} color="#e85ee5" />}
          className="absolute right-3 rounded-full p-3"
          onClick={onExpandClose}
        />
      )}
      <h3 className="text-center font-heebo text-lg text-slate-100">Mortgage Credit Curve</h3>
      <div className="relative" style={{ width, height: height + 20 }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(60,206,206,0.25) 0%, rgba(255,0,210,0) 70%)',
            opacity: 0.4,
          }}
        />
        <div
          ref={chartRef}
          style={{
            width,
            height,
            position: 'relative',
          }}
        />
      </div>
    </div>
  );
};
